// src/components/Features.tsx
import { Box, Button, Grid, MenuItem, Select, Stack, TextField, Typography, useMediaQuery, useTheme } from '@mui/material';
import { SelectChangeEvent } from '@mui/material/Select';
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { DURATIONS } from '../constants/booking';
import LocationSearch from './LocationSearch';

const features = [
  'Every Botel is ADT-secured & monitored 24/7',
  'Fast & Hassle-Free Check-in with QR Code Access!',
  'Secure Payment via Visa, Mastercard, Apple Pay, Stripe',
];

export default function Features() {
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isTablet = useMediaQuery(theme.breakpoints.down('md'));
  
  const [formData, setFormData] = useState({
    location: '',
    dateTime: '',
    duration: ''
  });
  const [errors, setErrors] = useState({
    location: '',
    dateTime: '',
    duration: ''
  });

  const validateForm = () => {
    const newErrors = {
      location: '',
      dateTime: '',
      duration: ''
    };
    let isValid = true;

    if (!formData.location) {
      newErrors.location = 'Location is required';
      isValid = false;
    }

    if (!formData.dateTime) {
      newErrors.dateTime = 'Date and time is required';
      isValid = false;
    }

    if (!formData.duration) {
      newErrors.duration = 'Duration is required';
      isValid = false;
    }

    setErrors(newErrors);
    return isValid;
  };

  const handleSelectChange = (field: string) => (event: SelectChangeEvent) => {
    setFormData(prev => ({
      ...prev,
      [field]: event.target.value
    }));
    if (errors[field as keyof typeof errors]) {
      setErrors(prev => ({
        ...prev,
        [field]: ''
      }));
    }
  };

  const handleInputChange = (field: string) => (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setFormData(prev => ({
      ...prev,
      [field]: event.target.value
    }));
    if (errors[field as keyof typeof errors]) {
      setErrors(prev => ({
        ...prev,
        [field]: ''
      }));
    }
  };

  const handleSubmit = () => {
    if (validateForm()) {
      // Convert form data to URL parameters
      const params = new URLSearchParams({
        location: formData.location,
        dateTime: formData.dateTime,
        duration: formData.duration
      });
      
      // Navigate to filtered botels page with parameters
      navigate(`/filtered-botels?${params.toString()}`);
    }
  };

  return (
    <Box sx={{ 
      py: { xs: 4, sm: 6 }, 
      px: { xs: 2, sm: 3, md: 4 },
      display: 'flex', 
      flexDirection: 'column', 
      alignItems: 'center', 
      justifyContent: 'center' 
    }}>
      <Grid 
        container 
        spacing={{ xs: 2, sm: 3, md: 4 }} 
        alignItems="center" 
        justifyContent="center" 
        sx={{ 
          mt: { xs: 2, sm: 3 }, 
          maxWidth: 1200, 
          mx: 'auto', 
          p: { xs: 2, sm: 3, md: 4 }, 
          borderRadius: 5, 
          boxShadow: 3, 
          bgcolor: '#131A21' 
        }}
      >
        <Grid size={{xs:12, sm:6, md:4}}>
          <LocationSearch
            value={formData.location}
            onChange={(value) => {
              setFormData(prev => ({ ...prev, location: value }));
              if (errors.location) {
                setErrors(prev => ({ ...prev, location: '' }));
              }
            }}
            error={errors.location}
          />
        </Grid>
        <Grid size={{xs:12, sm:6, md:4}}>
          <Typography variant="subtitle2" mb={1} color="#fff" sx={{ fontSize: { xs: 14, sm: 16 } }}>
            Date & Time
          </Typography>
          <TextField
            fullWidth
            type="datetime-local"
            value={formData.dateTime}
            onChange={handleInputChange('dateTime')}
            error={!!errors.dateTime}
            sx={{ 
              bgcolor: '#1a1f24', 
              borderRadius: 2, 
              input: { 
                color: '#fff',
                fontSize: { xs: 14, sm: 16 }
              } 
            }}
            InputLabelProps={{ shrink: true }}
          />
          {errors.dateTime && (
            <Typography color="error" variant="caption" sx={{ fontSize: { xs: 12, sm: 14 } }}>
              {errors.dateTime}
            </Typography>
          )}
        </Grid>
        <Grid size={{xs:12, sm:6, md:4}}>
          <Typography variant="subtitle2" mb={1} color="#fff" sx={{ fontSize: { xs: 14, sm: 16 } }}>
            Duration
          </Typography>
          <Select
            fullWidth
            displayEmpty
            value={formData.duration}
            onChange={handleSelectChange('duration')}
            error={!!errors.duration}
            sx={{ 
              bgcolor: '#1a1f24', 
              color: '#fff', 
              borderRadius: 2,
              fontSize: { xs: 14, sm: 16 }
            }}
            renderValue={selected => selected || 'Select Duration'}
          >
            <MenuItem disabled value="">
              Select Duration
            </MenuItem>
            {DURATIONS.map(d => (
              <MenuItem key={d.value} value={d.value}>{d.label}</MenuItem>
            ))}
          </Select>
          {errors.duration && (
            <Typography color="error" variant="caption" sx={{ fontSize: { xs: 12, sm: 14 } }}>
              {errors.duration}
            </Typography>
          )}
        </Grid>
        <Grid size={{xs:12}}>
          <Stack 
            direction={{ xs: 'column', sm: 'row' }} 
            alignItems="center" 
            justifyContent="center" 
            spacing={{ xs: 1, sm: 2 }}
            sx={{ mt: { xs: 2, sm: 3 } }}
          >
            <img src="icons/firePlus.svg" alt="fire icon" style={{ width: '24px', height: '24px' }} />
            <Typography 
              variant="subtitle1" 
              color="#e0e0e0" 
              gutterBottom 
              sx={{ 
                fontSize: { xs: 16, sm: 18, md: 20 },
                textAlign: { xs: 'center', sm: 'left' }
              }}
            >
              Only 2 Botels Left in Your Area – Reserve Now!
            </Typography>
          </Stack>
        </Grid>
        <Grid size={{xs:12}}>
          <Button 
            fullWidth 
            variant="contained" 
            onClick={handleSubmit}
            sx={{ 
              bgcolor: 'primary.main', 
              color: '#fff', 
              fontWeight: 700, 
              borderRadius: 2, 
              py: { xs: 1.5, sm: 2 }, 
              fontSize: { xs: 16, sm: 18 },
              mt: { xs: 2, sm: 3 }
            }}
          >
            Check Availability
          </Button>
        </Grid>
        <Grid size={{xs:12}}>
          <Stack 
            direction={{ xs: 'column', sm: 'row' }} 
            alignItems="center" 
            justifyContent="center" 
            spacing={{ xs: 1, sm: 2 }}
            sx={{ mt: { xs: 2, sm: 3 } }}
          >
            <img src="icons/keys-holder.svg" alt="keys icon" style={{ width: '24px', height: '24px' }} />
            <Typography 
              variant="subtitle1" 
              color="#e0e0e0" 
              gutterBottom 
              sx={{ 
                fontSize: { xs: 16, sm: 18, md: 20 },
                textAlign: { xs: 'center', sm: 'left' }
              }}
            >
              Only 2 Botels Left in Your Area – Reserve Now!
            </Typography>
          </Stack>
        </Grid>
      </Grid>
    </Box>
  );
}
